/**
 * useModal - 彈窗管理組合式函數
 *
 * 所有彈窗現在均走 Path B（native host 元件），不再使用 shared-web iframe。
 * balance modal 始終 native；paytable / rules / history 走 `isNativeHelp()`（白名單由
 * `REGISTERED_GAME_IDS` 派生 = 有 `src/games/<gameId>/content.ts` 就算數，沒有手寫常數）。
 */
import { computed, ref, type Ref } from 'vue'
import { type ModalInstance, generateModalId } from '../useHostBridge'
import { isNativeHelp } from '../config/nativeHelpGames'

// 所有遊戲都走 native 的 modal key
const NATIVE_MODALS = new Set(['balance'])

export interface ModalCallbacks {
  enqueueToGame: (
    action: string,
    payload: Record<string, unknown>,
    options?: { requiresAck?: boolean },
  ) => void
}

export function useModal(
  gameId: Ref<string | null>,
  callbacks: ModalCallbacks,
) {
  const modalStack = ref<ModalInstance[]>([])

  function isNativeModal(key: string): boolean {
    if (NATIVE_MODALS.has(key)) return true
    return isNativeHelp(key, gameId.value)
  }

  const activeBalanceModal = computed(() => {
    return modalStack.value.find((m) => m.key === 'balance') ?? null
  })

  const activeNativeHelpModal = computed(() => {
    return modalStack.value.find((m) => m.key !== 'balance' && isNativeModal(m.key)) ?? null
  })

  function closeAllModals() {
    if (modalStack.value.length === 0) return
    const modals = [...modalStack.value]
    modalStack.value = []
    modals.forEach((modal, index) => {
      const isLast = index === modals.length - 1
      callbacks.enqueueToGame(
        'MODAL_CLOSED',
        { modalId: modal.id, modalKey: modal.key },
        // 只有最後一則需要 ACK（確認 Cocos 已解鎖）；其餘 fire-and-forget
        isLast ? undefined : { requiresAck: false },
      )
    })
  }

  /** iframe reload 時靜默清空 stack（不送 MODAL_CLOSED，Cocos 已重啟） */
  function silentClearModals() {
    modalStack.value = []
  }

  function openModal(modalKey: string) {
    if (!gameId.value) return
    const currentModal = modalStack.value[modalStack.value.length - 1]
    if (currentModal?.key === modalKey) {
      callbacks.enqueueToGame(
        'MODAL_OPENED',
        { modalId: currentModal.id, modalKey },
        { requiresAck: false },
      )
      return
    }
    closeAllModals()

    const id = generateModalId()
    modalStack.value.push({ id, key: modalKey, title: modalKey })
    callbacks.enqueueToGame('MODAL_OPENED', { modalId: id, modalKey }, { requiresAck: false })
  }

  function closeModal(modalId?: string) {
    const modal = modalId
      ? modalStack.value.find((m) => m.id === modalId)
      : modalStack.value[modalStack.value.length - 1]

    if (!modal) return

    modalStack.value = modalStack.value.filter((m) => m.id !== modal.id)
    callbacks.enqueueToGame('MODAL_CLOSED', { modalId: modal.id, modalKey: modal.key })
  }

  return {
    modalStack,
    activeBalanceModal,
    activeNativeHelpModal,
    isNativeModal,
    openModal,
    closeModal,
    closeAllModals,
    silentClearModals,
  }
}
